import { IFeedbackFile, IFeedbackListData, IFeedbackSubmission } from '@common/interfaces/feedback'
import { combineReducers } from 'redux'
import { loadingArrayReducer } from '../../helpers/helpers'
import { IAction, IActionWithParams, IPaginationParams } from '../apiMiddleware'
import {
  ADD_FEEDBACK_INFO_FAILURE,
  ADD_FEEDBACK_INFO_REQUEST,
  ADD_FEEDBACK_INFO_SUCCESS,
  CREATE_FEEDBACK_REQUEST,
  CREATE_FEEDBACK_SUCCESS,
  CREATE_FEEDBACK_FAILURE,
  GET_FEEDBACK_SUBMISSION_SUCCESS,
  LIST_FEEDBACK_FILES_SUCCESS,
  LIST_FEEDBACK_SUCCESS,
  RESET_FEEDBACK_INFO,
  RESET_FEEDBACK_SUBMITTED,
  prefix,
} from './actions'

interface ISubmitParams {
  attemptId?: string
  /** Minted by the widget rather than apiMiddleware — see `isStaleSubmitResult`. */
  isLocal?: boolean
}

function isSubmitted(state = false, action: IAction): boolean {
  switch (action.type) {
    case CREATE_FEEDBACK_SUCCESS:
      return true
    case RESET_FEEDBACK_SUBMITTED:
    case CREATE_FEEDBACK_REQUEST:
      return false
    default:
      return state
  }
}

function submitError(state: string | null = null, action: IAction): string | null {
  switch (action.type) {
    case CREATE_FEEDBACK_FAILURE:
      return action.error?.message || 'Failed to submit feedback'
    case CREATE_FEEDBACK_REQUEST:
    case RESET_FEEDBACK_SUBMITTED:
      return null
    default:
      return state
  }
}

export interface IFeedbackFilesEntry {
  files: IFeedbackFile[]
  receivedAt: number
}

// Every submission seen, by id, so a modal opened from anywhere renders what's already loaded.
function submissionsById(
  state: Record<string, IFeedbackSubmission> = {},
  action: IAction,
): Record<string, IFeedbackSubmission> {
  switch (action.type) {
    case LIST_FEEDBACK_SUCCESS:
    case GET_FEEDBACK_SUBMISSION_SUCCESS: {
      const rows = (action.data as IFeedbackListData | undefined)?.data ?? []
      if (!rows.length) return state
      return { ...state, ...Object.fromEntries(rows.map((row) => [row.id, row])) }
    }
    default:
      return state
  }
}

// Signed file links per submission, with when they were signed, so a second viewer reuses them.
function filesById(
  state: Record<string, IFeedbackFilesEntry> = {},
  action: IActionWithParams<{ id?: string }>,
): Record<string, IFeedbackFilesEntry> {
  if (action.type !== LIST_FEEDBACK_FILES_SUCCESS || !action.params?.id) return state
  return {
    ...state,
    [action.params.id]: {
      files: (action.data as IFeedbackFile[] | undefined) ?? [],
      receivedAt: action.receivedAt ?? Date.now(),
    },
  }
}

function data(
  state: IFeedbackListData | null = null,
  action: IActionWithParams<IPaginationParams>,
): IFeedbackListData | null {
  switch (action.type) {
    case LIST_FEEDBACK_SUCCESS: {
      const payload = action.data as IFeedbackListData
      if (!action.params?.loadMore) {
        return payload
      }
      return {
        ...state,
        data: [...(state?.data || []), ...payload.data],
      }
    }
    default:
      return state
  }
}

// Tracks the last "Add Info" outcome so the detail view can show success /
// clear its textarea. Reset when a new add starts or the modal closes.
function infoAdded(state = false, action: IAction): boolean {
  switch (action.type) {
    case ADD_FEEDBACK_INFO_SUCCESS:
      return true
    case ADD_FEEDBACK_INFO_REQUEST:
    case RESET_FEEDBACK_INFO:
      return false
    default:
      return state
  }
}

function infoError(state: string | null = null, action: IAction): string | null {
  switch (action.type) {
    case ADD_FEEDBACK_INFO_FAILURE:
      return action.error?.message || 'Failed to add information'
    case ADD_FEEDBACK_INFO_REQUEST:
    case RESET_FEEDBACK_INFO:
      return null
    default:
      return state
  }
}

// The live submit attempt, or null. Cleared by whichever frame ends it.
function activeSubmitId(
  state: string | null = null,
  action: IActionWithParams<ISubmitParams>,
): string | null {
  switch (action.type) {
    case CREATE_FEEDBACK_REQUEST:
      return action.params?.attemptId ?? null
    case CREATE_FEEDBACK_SUCCESS:
    case CREATE_FEEDBACK_FAILURE:
    case RESET_FEEDBACK_SUBMITTED:
      return null
    default:
      return state
  }
}

const baseLoadingArray = loadingArrayReducer([prefix])

// `loadingArrayReducer` retires an entry only on a matching SUCCESS/FAILURE. Closing the panel
// retires the attempt with neither, and the answer that would have cleared it is dropped as stale
// by the time it lands — so without this the entry sits there for the rest of the session and the
// reopened panel renders the "Submitting" overlay with no form behind it.
const loadingArray = (state: string[] | undefined, action: IAction): string[] => {
  const next = baseLoadingArray(state, action)
  return action.type === RESET_FEEDBACK_SUBMITTED
    ? next.filter((item) => item !== CREATE_FEEDBACK_REQUEST)
    : next
}

const combined = combineReducers({
  loadingArray,
  isSubmitted,
  submitError,
  data,
  submissionsById,
  filesById,
  infoAdded,
  infoError,
  activeSubmitId,
})

type IFeedbackState = ReturnType<typeof combined>

// apiMiddleware has no cancellation, so a submit the widget gave up on keeps running and answers
// eventually. This must sit ABOVE `combineReducers`: `loadingArrayReducer` filters every entry
// matching the type, so a per-slice guard would still let a stale frame end the retry's loading.
const isStaleSubmitResult = (
  state: IFeedbackState,
  action: IActionWithParams<ISubmitParams>,
): boolean =>
  (action.type === CREATE_FEEDBACK_SUCCESS || action.type === CREATE_FEEDBACK_FAILURE) &&
  // A failure the widget minted itself has no request behind it — a submit that died before
  // `createFeedback` was dispatched leaves no REQUEST, and so no active id for it to match.
  // Exempting it puts the onus on the dispatcher: `failFeedbackSubmit` must only be called while
  // its report is still on screen, or the error lands on whatever the panel shows next. See the
  // report-key check in `FeedbackWidget`'s `submitFeedback`.
  !action.params?.isLocal &&
  (action.params?.attemptId ?? null) !== state.activeSubmitId

export default (
  state: IFeedbackState | undefined,
  action: IActionWithParams<ISubmitParams>,
): IFeedbackState => {
  if (state !== undefined && isStaleSubmitResult(state, action)) {
    return state
  }
  return combined(state, action)
}
